import type { DnsechoRow, IpDetails } from '@shared/api'
import { selfOperatorKey, toOperators } from './operators'

export type VerdictLevel = 'ok' | 'warn' | 'leak' | 'unknown'

export interface Verdict {
    level: VerdictLevel
    /** How many distinct operators saw the queries. */
    organisations: number
    countries: number
    /** Operator names, largest share first. */
    operators: string[]
    /** Present on `leak`: the operator that matches the user's own connection. */
    ispOperator?: string
}

/**
 * Classifies a completed leak test.
 *
 * The rules, agreed as product behaviour rather than derived from the data:
 *
 *   ok    every resolver's operator matches the connection's own operator
 *   warn  more than one operator, but none of them is the connection's
 *   leak  a resolver's operator IS the connection's operator while the exit
 *         address belongs to a different one -- the signature of a VPN that
 *         routes traffic but not DNS
 *
 * `leak` is the only outcome that makes a claim about someone's setup, so it is
 * deliberately the narrowest: it requires positive evidence that the user is
 * behind something (the exit operator differs) AND that their own ISP is still
 * answering lookups.
 */
export function classify(rows: DnsechoRow[], self: IpDetails | undefined): Verdict {
    if (rows.length === 0 || !self) {
        return { level: 'unknown', organisations: 0, countries: 0, operators: [] }
    }

    // The one canonical operator list -- the concentration chart draws from the
    // same function, so the two can never report different counts again.
    const operators = toOperators(rows, self)
    const countries = new Set(rows.map((r) => r.cc).filter(Boolean)).size
    const selfKey = selfOperatorKey(self)

    const base = {
        organisations: operators.length,
        countries,
        operators: operators.map((o) => o.name),
    }

    // Without the user's own ASN there is nothing to compare against, and
    // guessing would be worse than declining to answer.
    if (!selfKey || operators.length === 0) return { level: 'unknown', ...base }

    const matchesSelf = operators.some((o) => o.key === selfKey)
    const differsFromSelf = operators.some((o) => o.key !== selfKey)

    // Every resolver belongs to the same operator as the connection.
    if (!differsFromSelf) return { level: 'ok', ...base }

    // The connection's own operator is among the resolvers, while the exit
    // address belongs to someone else: traffic and DNS have diverged.
    if (matchesSelf) {
        return { level: 'leak', ...base, ispOperator: operators.find((o) => o.key === selfKey)?.name }
    }

    // Resolvers are all third parties. Common and often intentional (a chosen
    // public resolver), so this is a caution rather than a finding.
    return { level: 'warn', ...base }
}
